import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { FormInstance } from "antd";
import { DistrictConfigurationInjection } from "../../di/DistrictConfigurationInjection";
import { DistrictConfigurationListRequestEntity } from "../../domain/entities/DistrictConfigurationListRequestEntity";
import { DistrictConfigurationFormRequestEntity } from "../../domain/entities/DistrictConfigurationFormRequestEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";
import { IDistrictConfigurationObservedState } from "../interfaces/IDistrictConfiguration";

// ─── Input ────────────────────────────────────────────────────────────────────

class DistrictConfigurationInput implements Disposable {
  public fetchList = new BehaviorSubject<DistrictConfigurationListRequestEntity>({
    page: 0,
    limit: 10,
  });
  public fetchDetail = new BehaviorSubject<string>("");
  public createDistrict = new BehaviorSubject<DistrictConfigurationFormRequestEntity>({});
  public updateDistrict = new BehaviorSubject<DistrictConfigurationFormRequestEntity>({});
  public deleteDistrict = new BehaviorSubject<string>("");

  public fetchProvinces = new BehaviorSubject<void>(undefined);

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchDetail.complete();
    this.createDistrict.complete();
    this.updateDistrict.complete();
    this.deleteDistrict.complete();
    this.fetchProvinces.complete();
  }
}

// ─── Output ───────────────────────────────────────────────────────────────────

class DistrictConfigurationOutput implements Disposable {
  public onError = new BehaviorSubject<string>("");
  public actionSuccess = new BehaviorSubject<
    "create" | "update" | "delete" | null
  >(null);
  public onGetDetailSuccess = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.actionSuccess.complete();
    this.onGetDetailSuccess.complete();
  }
}

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class DistrictConfigurationViewModel extends BasedObservedViewModel<
  DistrictConfigurationInput,
  DistrictConfigurationOutput,
  IDistrictConfigurationObservedState
> {
  constructor() {
    super(new DistrictConfigurationInput(), new DistrictConfigurationOutput());
  }

  private useCase = DistrictConfigurationInjection.getUseCase();

  protected override createObservedState(): IDistrictConfigurationObservedState {
    return {
      loading: false,
      formLoading: false,
      detailLoading: false,
      list: [],
      detail: {},
      paging: { page: 0, limit: 10, total: 0 },
      provinceOptions: [],
    };
  }

  public buildTableQuery(
    pagination: any,
    sorter: any,
    filterForm: React.RefObject<FormInstance<any>>,
    prevParams: any,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    let order = prevParams?.order;
    if (sorter && sorter.field && sorter.order) {
      const direction = sorter.order === "ascend" ? "ASC" : "DESC";
      order = `${sorter.field} ${direction}`;
    }

    const query = {
      ...prevParams,
      ...formValues,
      page: (pagination?.current ?? 1) - 1,
      limit: pagination?.pageSize ?? 10,
      order,
    };

    return {
      query,
    };
  }

  protected override transform(): void {
    // Fetch list
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.setObservedState({ loading: true });
        const res = await this.useCase.getDistrictConfigurationList(params);
        if (res) {
          this.setObservedState({
            list: res.data || [],
            paging: res.pagination || {
              page: params.page || 0,
              limit: params.limit || 10,
              total: 0,
            },
          });
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    // Fetch detail
    this.skipFirstInitial(this.input.fetchDetail, async (id) => {
      if (!id) return;
      try {
        this.setObservedState({ detailLoading: true });
        const res = await this.useCase.getDistrictConfigurationDetail(id);
        if (res?.data) {
          this.setObservedState({ detail: res.data });
          this.output.onGetDetailSuccess.next(true);
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ detailLoading: false });
      }
    });

    // Create district
    this.skipFirstInitial(this.input.createDistrict, async (body) => {
      if (!body || Object.keys(body).length === 0) return;
      try {
        this.setObservedState({ formLoading: true });
        const res = await this.useCase.createDistrictConfiguration(body);
        if (res?.data) {
          this.output.actionSuccess.next("create");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ formLoading: false });
      }
    });

    // Update district
    this.skipFirstInitial(this.input.updateDistrict, async (body) => {
      if (!body || Object.keys(body).length === 0) return;
      try {
        this.setObservedState({ formLoading: true });
        const res = await this.useCase.updateDistrictConfiguration(body);
        if (res?.data) {
          this.output.actionSuccess.next("update");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ formLoading: false });
      }
    });

    // Delete district
    this.skipFirstInitial(this.input.deleteDistrict, async (id) => {
      if (!id) return;
      try {
        this.setObservedState({ loading: true });
        const res = await this.useCase.deleteDistrictConfiguration(id);
        if (res) {
          this.output.actionSuccess.next("delete");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    // Fetch Provinces
    this.skipFirstInitial(this.input.fetchProvinces, async () => {
      try {
        const res = await this.useCase.getProvinceList();
        if (res?.data) {
          const options = res.data.map((p) => ({
            label: p.fullName || p.name || "",
            value: p.code || "",
          }));
          this.setObservedState({ provinceOptions: options });
        }
      } catch (error) {
        console.error(error);
      }
    });
  }
}
